import React, { useState, useEffect } from 'react';
import modelsService from '../../services/modelsService';
import { Alert, Snackbar } from '@mui/material';
import {
  Cpu,
  Box as BoxIcon,
  Zap,
  Brain,
  Terminal,
  Globe,
  Layers,
} from 'lucide-react';
import styles from './Models.module.scss';

const ProviderBrandIcon = ({ name }) => {
  const n = (name || '').toLowerCase();
  const size = 20;
  const stroke = 2;
  if (n.includes('openai')) return <Zap size={size} strokeWidth={stroke} />;
  if (n.includes('anthropic')) return <Brain size={size} strokeWidth={stroke} />;
  if (n.includes('google') || n.includes('gemini')) return <Globe size={size} strokeWidth={stroke} />;
  if (n.includes('ollama')) return <Terminal size={size} strokeWidth={stroke} />;
  return <Layers size={size} strokeWidth={stroke} />;
};

const Models = () => {
  const [providers, setProviders] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

  const showToast = (message, severity = 'info') => {
    setToast({ open: true, message, severity });
  };

  const handleToastClose = () => {
    setToast({ open: false, message: '', severity: 'info' });
  };

  const getProviderLabel = (providerId) =>
    providers.find((p) => p.id === providerId)?.display_name || 'Unknown';

  const getModelContext = (model) =>
    model.context_window ??
    model.max_context_tokens ??
    model.context ??
    model.metadata?.context ??
    null;

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const [providerData, modelData] = await Promise.all([
          modelsService.getProviders(true),
          modelsService.getModels(true),
        ]);
        if (!cancelled) {
          setProviders(Array.isArray(providerData) ? providerData : []);
          setModels(Array.isArray(modelData) ? modelData : []);
        }
      } catch (err) {
        if (!cancelled) {
          showToast(`Failed to load providers or models: ${err.message}`, 'error');
          setProviders([]);
          setModels([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className={styles.models}>
      <div className={styles.pageShell}>
        <header className={styles.pageHeaderCard}>
          <h1 className={styles.pageTitle}>LLM Providers &amp; Models</h1>
          <p className={styles.pageSubtitle}>
            Available providers and models (read-only)
          </p>
        </header>

        <section className={styles.sectionCard}>
          <div className={styles.sectionToolbar}>
            <div className={styles.sectionHeading}>
              <div className={styles.sectionIconWrap} aria-hidden>
                <Cpu strokeWidth={2} />
              </div>
              <h2 className={styles.sectionHeadingTitle}>LLM Providers</h2>
            </div>
          </div>

          {loading ? (
            <div className={styles.emptyHint}>Loading providers…</div>
          ) : providers.length === 0 ? (
            <div className={styles.emptyHint}>No providers configured.</div>
          ) : (
            <div className={styles.providersGrid}>
              {providers.map((provider) => (
                <div key={provider.id} className={styles.providerRow}>
                  <div className={styles.providerMain}>
                    <div className={styles.providerIconBox}>
                      <ProviderBrandIcon name={provider.name} />
                    </div>
                    <div className={styles.providerText}>
                      <div className={styles.providerTitleRow}>
                        <p className={styles.providerDisplayName}>
                          {provider.display_name || provider.name}
                        </p>
                        <span className={styles.providerSlug}>({provider.name})</span>
                      </div>
                      {provider.description && (
                        <p className={styles.providerDesc} title={provider.description}>
                          {provider.description}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className={styles.providerMeta}>
                    <span
                      className={`${styles.statusPill} ${
                        provider.is_active ? styles.active : styles.inactive
                      }`}
                    >
                      {provider.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className={styles.sectionCard}>
          <div className={styles.sectionToolbar}>
            <div className={styles.sectionHeading}>
              <div className={styles.sectionIconWrap} aria-hidden>
                <BoxIcon strokeWidth={2} />
              </div>
              <h2 className={styles.sectionHeadingTitle}>LLM Models</h2>
            </div>
          </div>

          {loading ? (
            <div className={styles.emptyHint}>Loading models…</div>
          ) : models.length === 0 ? (
            <div className={styles.emptyHint}>No models configured.</div>
          ) : (
            <div className={styles.modelsGrid}>
              {models.map((model) => {
                const ctx = getModelContext(model);
                return (
                  <article key={model.id} className={styles.modelCard}>
                    <div className={styles.modelCardTop}>
                      <div>
                        <h3 className={styles.modelName}>{model.name}</h3>
                        <p className={styles.modelProvider}>{getProviderLabel(model.provider_id)}</p>
                      </div>
                      <span
                        className={`${styles.statusPill} ${model.is_active ? styles.active : styles.inactive}`}
                      >
                        {model.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    {model.description && (
                      <p className={styles.modelDesc}>{model.description}</p>
                    )}
                    <div className={styles.modelFooter}>
                      <div className={styles.contextRow}>
                        <span className={styles.contextLabel}>Context:</span>
                        <span className={styles.contextValue}>
                          {ctx != null && ctx !== '' ? String(ctx) : '—'}
                        </span>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </div>

      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleToastClose} severity={toast.severity}>
          {toast.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Models;
